/**
 * materialManager.js - Material management, colors, and textures
 */

import * as THREE from 'three';
import * as state from './state.js';
import { getMaterialsArray, isDecalMaterial } from './utils.js';
import { requestRender } from './sceneManager.js';
import { updateAllMaterialsWithGraphicLayers } from './decalManager.js';

// ===== MATERIAL UTILITIES =====

// Updated sowohl sideMats als auch aktuelle Mesh-Materialien für eine Seite
export function updateSideMaterials(groupName, sideName, updateFn, options = {}) {
    const {skipTextureOverride = false, disposeMap = false} = options;

    // Update sideMats
    const groups = window._configGroups;
    const sideData = groups?.get(groupName)?.sides?.get(sideName);
    if (sideData?.mats) {
        let count = 0;
        Array.from(sideData.mats).forEach(mat => {
            if (skipTextureOverride && mat.userData.hasTextureOverride) {
                return;
            }

            if (disposeMap && mat.map) {
                mat.map.dispose();
                mat.map = null;
            }

            updateFn(mat);
            count++;
        });
    }

    // Update aktuelle Mesh-Materialien
    const meshesFromSideData = sideData?.meshes;
    const meshesFromGlobalMap = state.sideMeshes.get(groupName)?.get(sideName);
    let meshesForSide = meshesFromSideData || meshesFromGlobalMap;

    // Fallback: Wenn die gespeicherten Meshes ungültig sind, durchsuche die Scene
    if (!meshesForSide || meshesForSide.size === 0 || Array.from(meshesForSide).some(m => !m.material || !m.parent)) {
        const meshesFromScene = new Set();
        const marker = `${groupName}_${sideName}`;

        state.scene.traverse(obj => {
            if (obj.isMesh && obj.name && obj.name.includes(marker)) {
                meshesFromScene.add(obj);
            }
        });

        if (meshesFromScene.size > 0) {
            meshesForSide = meshesFromScene;

            // Aktualisiere auch die gespeicherten Referenzen
            if (sideData) {
                sideData.meshes = meshesFromScene;
            }
            if (meshesFromGlobalMap) {
                meshesFromGlobalMap.clear();
                meshesFromScene.forEach(m => meshesFromGlobalMap.add(m));
            }
        }
    }


    if (meshesForSide && meshesForSide.size > 0) {
        let meshCount = 0;
        meshesForSide.forEach(mesh => {
            const materialsArray = getMaterialsArray(mesh.material);

            materialsArray.forEach((mat, matIndex) => {
                if (!mat) {
                    return;
                }
                if (skipTextureOverride && mat.userData.hasTextureOverride) {
                    return;
                }

                if (disposeMap && mat.map) {
                    mat.map.dispose();
                    mat.map = null;
                }

                updateFn(mat);
            });

            if (Array.isArray(mesh.material)) {
                mesh.material = [...mesh.material];
            } else {
            }
            meshCount++;
        });
    }
}

// ===== COLOR APPLICATION =====

export function applyGroupColor(groupName, color) {
    const groups = window._configGroups;
    if (!groups || !groups.has(groupName)) {
        return;
    }
    const groupData = groups.get(groupName);
    groupData.color = color;


    let matsCount = 0;
    for (const [sideName, sideData] of groupData.sides.entries()) {
        if (!sideData.color) {
            const matsArray = Array.from(sideData.mats);
            matsArray.forEach(m => {
                if (m.userData.hasTextureOverride) {
                    return;
                }

                // Entferne IMMER die Map bei Farbanwendung (wichtig für Holz -> Farbe)
                if (m.map) {
                    m.map.dispose();
                    m.map = null;
                }

                // Speichere Original-Materialwerte beim ersten Mal
                if (m.userData.originalMetalness === undefined) {
                    m.userData.originalMetalness = m.metalness;
                    m.userData.originalRoughness = m.roughness;
                }

                m.color.set(color);
                if (m.emissive) {
                    m.emissive.set(0x000000);
                }
                // Behalte Original-Materialwerte
                m.metalness = m.userData.originalMetalness;
                m.roughness = m.userData.originalRoughness;
                m.needsUpdate = true;
                matsCount++;
            });
        }
    }

    let meshCount = 0;
    for (const [sideName, sideData] of groupData.sides.entries()) {
        if (!sideData.color) {
            const meshesForSide = state.sideMeshes.get(groupName)?.get(sideName);
            if (meshesForSide) {
                meshesForSide.forEach(mesh => {
                    getMaterialsArray(mesh.material).forEach(mat => {
                        if (mat && mat.color && !mat.userData.hasTextureOverride) {
                            // Entferne IMMER die Map bei Farbanwendung (wichtig für Holz -> Farbe)
                            if (mat.map) {
                                mat.map.dispose();
                                mat.map = null;
                            }

                            // Speichere Original-Materialwerte beim ersten Mal
                            if (mat.userData.originalMetalness === undefined) {
                                mat.userData.originalMetalness = mat.metalness;
                                mat.userData.originalRoughness = mat.roughness;
                            }

                            mat.color.set(color);
                            if (mat.emissive) {
                                mat.emissive.set(0x000000);
                            }
                            // Behalte Original-Materialwerte
                            mat.metalness = mat.userData.originalMetalness;
                            mat.roughness = mat.userData.originalRoughness;

                            // Alpha/Transparenz Handling für Glas
                            // Speichere Original-Opacity beim ersten Mal
                            if (mat.userData.originalOpacity === undefined) {
                                mat.userData.originalOpacity = mat.opacity;
                            }

                            if (groupData.keepAlpha) {
                                // Behalte Original-Transparenz (für Glas)
                                mat.opacity = mat.userData.originalOpacity;
                                mat.transparent = mat.userData.originalOpacity < 1;
                            } else {
                                // Mache opak (Standard-Verhalten)
                                mat.opacity = 1;
                                mat.transparent = false;
                            }

                            mat.needsUpdate = true;
                        }
                    });

                    if (Array.isArray(mesh.material)) {
                        mesh.material = [...mesh.material];
                    }
                    meshCount++;
                });
            }
        }
    }

    requestRender();
}

export function applySideColor(groupName, sideName, color) {
    const groups = window._configGroups;
    if (!groups || !groups.has(groupName)) {
        return;
    }

    const groupData = groups.get(groupName);
    const sideData = groupData.sides.get(sideName);
    if (!sideData) {
        return;
    }

    sideData.color = color;

    updateSideMaterials(groupName, sideName, (mat) => {
        mat.userData.hasTextureOverride = false;

        // Speichere Original-Materialwerte beim ersten Mal
        if (mat.userData.originalMetalness === undefined) {
            mat.userData.originalMetalness = mat.metalness;
            mat.userData.originalRoughness = mat.roughness;
        }

        mat.color.set(color);
        if (mat.emissive) {
            mat.emissive.set(0x000000);
        }
        // Behalte Original-Materialwerte
        mat.metalness = mat.userData.originalMetalness;
        mat.roughness = mat.userData.originalRoughness;

        // Alpha/Transparenz Handling für Glas
        // Speichere Original-Opacity beim ersten Mal
        if (mat.userData.originalOpacity === undefined) {
            mat.userData.originalOpacity = mat.opacity;
        }

        if (sideData.keepAlpha) {
            // Behalte Original-Transparenz (für Glas)
            mat.opacity = mat.userData.originalOpacity;
            mat.transparent = mat.userData.originalOpacity < 1;
        } else {
            // Mache opak (Standard-Verhalten)
            mat.opacity = 1;
            mat.transparent = false;
        }

        mat.needsUpdate = true;
    }, {disposeMap: true});

    requestRender();
}

// ===== CAMO TEXTURE =====

export function getCamoTexture(textureLoader, callback) {
    if (state.cachedCamoTexture) {
        state.cachedCamoTexture.repeat.set(0.66, 0.66);
        callback(state.cachedCamoTexture);
        return;
    }

    const texturePath = '/userdata/files/models/camo-texture.jpg';
    textureLoader.load(texturePath, (camoTexture) => {
        camoTexture.wrapS = camoTexture.wrapT = THREE.RepeatWrapping;
        camoTexture.repeat.set(0.66, 0.66);
        camoTexture.colorSpace = THREE.SRGBColorSpace;
        state.setCachedCamoTexture(camoTexture);
        callback(camoTexture);
    });
}

export function applyGroupCamoTexture(groupName, textureLoader) {
    getCamoTexture(textureLoader, (camoTexture) => {
        const groups = window._configGroups;
        if (!groups || !groups.has(groupName)) {
            return;
        }
        const groupData = groups.get(groupName);
        groupData.color = 'camo';

        for (const [sideName, sideData] of groupData.sides.entries()) {
            if (!sideData.color) {
                const matsArray = Array.from(sideData.mats);
                matsArray.forEach(m => {
                    if (m.userData.hasTextureOverride) return;

                    // Speichere Original-Materialwerte beim ersten Mal
                    if (m.userData.originalMetalness === undefined) {
                        m.userData.originalMetalness = m.metalness;
                        m.userData.originalRoughness = m.roughness;
                    }

                    m.map = camoTexture;
                    m.color.set(0xffffff);
                    if (m.emissive) {
                        m.emissive.set(0x000000);
                    }
                    // Behalte Original-Materialwerte
                    m.metalness = m.userData.originalMetalness;
                    m.roughness = m.userData.originalRoughness;
                    m.needsUpdate = true;
                });
            }
        }

        for (const [sideName, sideData] of groupData.sides.entries()) {
            if (!sideData.color) {
                const meshesForSide = state.sideMeshes.get(groupName)?.get(sideName);
                if (meshesForSide) {
                    meshesForSide.forEach(mesh => {
                        getMaterialsArray(mesh.material).forEach(mat => {
                            if (mat && !mat.userData.hasTextureOverride) {
                                // Speichere Original-Materialwerte beim ersten Mal
                                if (mat.userData.originalMetalness === undefined) {
                                    mat.userData.originalMetalness = mat.metalness;
                                    mat.userData.originalRoughness = mat.roughness;
                                }

                                mat.map = camoTexture;
                                mat.color.set(0xffffff);
                                if (mat.emissive) {
                                    mat.emissive.set(0x000000);
                                }
                                // Behalte Original-Materialwerte
                                mat.metalness = mat.userData.originalMetalness;
                                mat.roughness = mat.userData.originalRoughness;
                                mat.needsUpdate = true;
                            }
                        });

                        if (Array.isArray(mesh.material)) {
                            mesh.material = [...mesh.material];
                        }
                    });
                }
            }
        }

        requestRender();
    });
}

export function applySideCamoTexture(groupName, sideName, textureLoader) {
    getCamoTexture(textureLoader, (camoTexture) => {
        const groups = window._configGroups;
        if (!groups || !groups.has(groupName)) {
            return;
        }
        const groupData = groups.get(groupName);
        const sideData = groupData.sides.get(sideName);
        if (sideData) {
            sideData.color = 'camo';
        }

        state.sideMats.get(groupName)?.get(sideName)?.forEach(m => {
            if (m.userData.hasTextureOverride) return;

            // Speichere Original-Materialwerte beim ersten Mal
            if (m.userData.originalMetalness === undefined) {
                m.userData.originalMetalness = m.metalness;
                m.userData.originalRoughness = m.roughness;
            }

            m.map = camoTexture;
            m.color.set(0xffffff);
            if (m.emissive) {
                m.emissive.set(0x000000);
            }
            // Behalte Original-Materialwerte
            m.metalness = m.userData.originalMetalness;
            m.roughness = m.userData.originalRoughness;
            m.needsUpdate = true;
        });

        const meshesForSide = state.sideMeshes.get(groupName)?.get(sideName);
        if (meshesForSide) {
            meshesForSide.forEach(mesh => {
                getMaterialsArray(mesh.material).forEach(mat => {
                    if (mat && !mat.userData.hasTextureOverride) {
                        // Speichere Original-Materialwerte beim ersten Mal
                        if (mat.userData.originalMetalness === undefined) {
                            mat.userData.originalMetalness = mat.metalness;
                            mat.userData.originalRoughness = mat.roughness;
                        }

                        mat.map = camoTexture;
                        mat.color.set(0xffffff);
                        if (mat.emissive) {
                            mat.emissive.set(0x000000);
                        }
                        // Behalte Original-Materialwerte
                        mat.metalness = mat.userData.originalMetalness;
                        mat.roughness = mat.userData.originalRoughness;
                        mat.needsUpdate = true;
                    }
                });
            });
        }

        requestRender();
    });
}

// ===== RESET FUNCTIONS =====

export function resetGroupToOriginal(groupName) {
    const groups = window._configGroups;
    if (!groups || !groups.has(groupName)) {
        return;
    }
    const groupData = groups.get(groupName);
    groupData.color = null;

    for (const [sideName, sideData] of groupData.sides.entries()) {
        if (!sideData.color) {
            const meshesForSide = state.sideMeshes.get(groupName)?.get(sideName);
            if (meshesForSide) {
                meshesForSide.forEach(mesh => {
                    const origMaterials = state.originalMaterials.get(mesh.uuid);
                    if (origMaterials) {
                        mesh.material = Array.isArray(mesh.material)
                            ? origMaterials.map(m => m.clone())
                            : origMaterials[0].clone();
                    }
                });
            }
        }
    }

    requestRender();
}

export function resetSideToOriginal(groupName, sideName) {
    const meshesForSide = state.sideMeshes.get(groupName)?.get(sideName);
    if (meshesForSide) {
        meshesForSide.forEach(mesh => {
            const origMaterials = state.originalMaterials.get(mesh.uuid);
            if (origMaterials) {
                mesh.material = Array.isArray(mesh.material)
                    ? origMaterials.map(m => m.clone())
                    : origMaterials[0].clone();
            }
        });

        // Wichtig: Entferne initialized-Flag damit Decals neu angewendet werden
        meshesForSide.forEach(mesh => {
            state.initializedMeshes?.delete(mesh.uuid);
        });
    }

    const groups = window._configGroups;
    if (groups && groups.has(groupName)) {
        const groupData = groups.get(groupName);
        const sideData = groupData.sides.get(sideName);
        if (sideData) {
            sideData.color = null;
        }
    }

    // Decals neu anwenden nach Reset
    updateAllMaterialsWithGraphicLayers();

    requestRender();
}

export function resetSideOverrides(groupName, sideName) {
    const groups = window._configGroups;
    if (!groups?.has(groupName)) return;
    const groupData = groups.get(groupName);
    const sideData = groupData.sides.get(sideName);
    if (!sideData) return;

    sideData.color = null;

    updateSideMaterials(groupName, sideName, (mat) => {
        if (mat.userData.originalColor) {
            mat.color.copy(mat.userData.originalColor);
        } else {
            const inherited = groupData.color || '#ffffff';
            mat.color.set(inherited);
        }

        if (mat.emissive) {
            mat.emissive.set(0x000000);
        }

        // Behalte Original-Materialwerte
        if (mat.userData.originalMetalness !== undefined) {
            mat.metalness = mat.userData.originalMetalness;
        }
        if (mat.userData.originalRoughness !== undefined) {
            mat.roughness = mat.userData.originalRoughness;
        }

        mat.needsUpdate = true;
    }, {skipTextureOverride: true, disposeMap: true});

    requestRender();
}

export function resetSideColor(groupName, sideName) {
    resetSideToOriginal(groupName, sideName);
}
